<!-- Ported from packages/website/src/components/DatePicker/DatePicker.vue -->
<template>
  <div class="flex flex-col gap-3" data-testid="datepicker-calendar">
    <DatepickerMonthYearPanel
      v-if="monthYearView"
      v-model:view="monthYearView"
      :year="monthYearPanelYear"
      :selected-month="{
        month: firstVisibleMonth.getMonth(),
        year: firstVisibleMonth.getFullYear(),
      }"
      :selected-year="firstVisibleMonth.getFullYear()"
      :min-date="minDate"
      :max-date="maxDate"
      @month-selected="handleMonthSelected"
    />
    <div v-else class="flex flex-col gap-6 md:flex-row">
      <DatepickerMonth
        v-for="(month, index) in visibleMonths"
        :key="month.getTime()"
        :class="weekNumbers ? 'w-72' : 'w-64'"
        :month="month"
        :index="index"
        :start-date="startDate"
        :end-date="displayedEndDate"
        :week-numbers="weekNumbers"
        :is-day-disabled="isDisabled"
        :is-day-highlighted="highlightedDates"
        @day-selected="handleDayClick"
        @day-mouse-over="hoverDate = $event"
        @day-mouse-out="hoverDate = undefined"
        @title-click="openMonthYearPanel(month)"
      >
        <template v-if="index === 0" #previous>
          <button
            type="button"
            class="scroll-button"
            :disabled="!canScroll(-1)"
            :aria-label="t('previousMonth')"
            data-testid="month-scroll-left"
            @click="changeScrollPosition(-1)"
          >
            <ArrowLeftIcon class="size-4" />
          </button>
        </template>
        <template v-if="index === visibleMonths.length - 1" #next>
          <button
            type="button"
            class="scroll-button"
            :disabled="!canScroll(1)"
            :aria-label="t('nextMonth')"
            data-testid="month-scroll-right"
            @click="changeScrollPosition(1)"
          >
            <ArrowRightIcon class="size-4" />
          </button>
        </template>
      </DatepickerMonth>
    </div>
    <!-- w-0 min-w-full: the months set the panel width, the times wrap if they don't fit. -->
    <div
      v-if="enableTimePicker && !monthYearView"
      class="flex w-0 min-w-full flex-wrap justify-center gap-x-3 gap-y-2 border-t border-obsidian-200 pt-3"
    >
      <template v-if="range">
        <DatepickerTimeInput
          v-if="startDate"
          :model-value="startDate"
          :label="t('start')"
          test-id-suffix="-start"
          @update:model-value="handleRangeTimeChange('start', $event)"
        />
        <DatepickerTimeInput
          v-if="endDate"
          :model-value="endDate"
          :label="t('end')"
          test-id-suffix="-end"
          @update:model-value="handleRangeTimeChange('end', $event)"
        />
      </template>
      <DatepickerTimeInput
        v-else
        :model-value="startDate ?? draftTime"
        :label="t('time')"
        @update:model-value="handleSingleTimeChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-vue-next';
import {
  addMonths,
  differenceInCalendarMonths,
  eachDayOfInterval,
  isBefore,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import DatepickerMonth from './DatepickerMonth.vue';
import DatepickerMonthYearPanel from './DatepickerMonthYearPanel.vue';
import DatepickerTimeInput from './DatepickerTimeInput.vue';
import {
  applyTimeOfDay,
  clampDate,
  isDayDisabled,
  type DayConstraints,
  type DisabledDates,
  type MonthValue,
} from './datepicker-utils';

const props = withDefaults(
  defineProps<{
    /** Selected date in single mode, range start in range mode. */
    startDate?: Date | null;
    endDate?: Date | null;
    range?: boolean;
    enableTimePicker?: boolean;
    months?: number;
    weekNumbers?: boolean;
    minDate?: Date | null;
    maxDate?: Date | null;
    disabledDates?: DisabledDates;
    highlightedDates?: (date: Date) => boolean;
    /** Restart the range when it would span a disabled day. */
    noDisabledRange?: boolean;
  }>(),
  { startDate: null, endDate: null, months: 1, minDate: null, maxDate: null },
);

const emit = defineEmits<{
  (e: 'select', value: Date): void;
  /** `endDate` is null when a new start was picked after the previous end. */
  (e: 'selectRange', startDate: Date, endDate: Date | null): void;
  (e: 'daySelected'): void;
}>();

/** Which edge of the range the next day click sets, like the website booking bar. */
const activeEdge = defineModel<'start' | 'end'>('activeEdge', { default: 'start' });

const { t } = useI18n();

const constraints = computed<DayConstraints>(() => ({
  minDate: props.minDate,
  maxDate: props.maxDate,
  disabledDates: props.disabledDates,
}));

const isDisabled = (date: Date) => isDayDisabled(date, constraints.value);

const hoverDate = ref<Date>();

// A new start after the end clears it; the next end pick keeps that end's time of day.
let lastEndDate = props.endDate;
watch(
  () => props.endDate,
  (endDate) => {
    if (endDate) lastEndDate = endDate;
  },
);
const draftTime = ref(applyTimeOfDay(new Date()));

const displayedEndDate = computed(() => {
  if (!props.range) return props.startDate;
  if (props.endDate || !props.startDate) return props.endDate;
  // Preview the range while the end is still open, like the website.
  return hoverDate.value && !isBefore(hoverDate.value, startOfDay(props.startDate))
    ? hoverDate.value
    : null;
});

const finalizeDate = (date: Date) =>
  props.enableTimePicker ? clampDate(date, constraints.value) : date;

const hasDisabledDayBetween = (start: Date, end: Date) =>
  eachDayOfInterval({ start: startOfDay(start), end: startOfDay(end) }).some(isDisabled);

const handleDayClick = (date: Date): void => {
  if (!props.range) {
    const timeSource = props.startDate ?? (props.enableTimePicker ? draftTime.value : null);
    emit('select', finalizeDate(applyTimeOfDay(date, timeSource)));
    emit('daySelected');
    return;
  }

  const { startDate, endDate } = props;
  const isNewStart =
    activeEdge.value === 'start' ||
    !startDate ||
    isBefore(startOfDay(date), startOfDay(startDate)) ||
    (props.noDisabledRange && hasDisabledDayBetween(startDate, date));

  if (isNewStart) {
    const newStartDate = finalizeDate(applyTimeOfDay(date, startDate));
    const keepsEndDate =
      !!endDate &&
      !isBefore(startOfDay(endDate), startOfDay(newStartDate)) &&
      !(props.noDisabledRange && hasDisabledDayBetween(newStartDate, endDate));
    emit('selectRange', newStartDate, keepsEndDate ? getLaterDate(newStartDate, endDate) : null);
    activeEdge.value = 'end';
    return;
  }

  const newEndDate = finalizeDate(applyTimeOfDay(date, lastEndDate ?? startDate));
  emit('selectRange', startDate, getLaterDate(startDate, newEndDate));
  emit('daySelected');
};

const getLaterDate = (a: Date, b: Date) => (isBefore(b, a) ? a : b);

const handleSingleTimeChange = (value: Date) => {
  if (!props.startDate) {
    draftTime.value = value;
    return;
  }
  emit('select', finalizeDate(value));
};

const handleRangeTimeChange = (edge: 'start' | 'end', value: Date) => {
  if (!props.startDate) return;
  const startDate = finalizeDate(edge === 'start' ? value : props.startDate);
  const endDate = edge === 'end' ? value : props.endDate;
  emit('selectRange', startDate, endDate ? getLaterDate(startDate, finalizeDate(endDate)) : null);
};

const firstVisibleMonth = ref(getInitialFirstVisibleMonth());
const visibleMonths = computed(() =>
  Array.from({ length: props.months }, (_, i) => addMonths(firstVisibleMonth.value, i)),
);

function getInitialFirstVisibleMonth() {
  const { startDate, endDate, months } = props;
  if (activeEdge.value === 'end' && endDate) {
    const startsInWindow = startDate && differenceInCalendarMonths(endDate, startDate) < months;
    return clampFirstVisibleMonth(
      startsInWindow ? startOfMonth(startDate) : addMonths(startOfMonth(endDate), 1 - months),
    );
  }
  return clampFirstVisibleMonth(startOfMonth(startDate ?? endDate ?? new Date()));
}

function clampFirstVisibleMonth(month: Date) {
  if (props.maxDate) {
    const lastAllowedFirstMonth = addMonths(startOfMonth(props.maxDate), 1 - props.months);
    if (isBefore(lastAllowedFirstMonth, month)) month = lastAllowedFirstMonth;
  }
  if (props.minDate && isBefore(month, startOfMonth(props.minDate))) {
    month = startOfMonth(props.minDate);
  }
  return month;
}

const canScroll = (distance: number) =>
  differenceInCalendarMonths(
    clampFirstVisibleMonth(addMonths(firstVisibleMonth.value, distance)),
    firstVisibleMonth.value,
  ) !== 0;

const changeScrollPosition = (distance: number) => {
  firstVisibleMonth.value = clampFirstVisibleMonth(addMonths(firstVisibleMonth.value, distance));
};

// Follow external changes (e.g. typed input) when the selection is outside the visible months.
watch(
  () => props.startDate,
  (startDate) => {
    if (!startDate) return;
    const offset = differenceInCalendarMonths(startDate, firstVisibleMonth.value);
    if (offset < 0 || offset >= props.months) {
      firstVisibleMonth.value = clampFirstVisibleMonth(startOfMonth(startDate));
    }
  },
);

const monthYearView = ref<'months' | 'years' | null>(null);
const monthYearPanelYear = ref(firstVisibleMonth.value.getFullYear());

const openMonthYearPanel = (month: Date) => {
  monthYearPanelYear.value = month.getFullYear();
  monthYearView.value = 'months';
};

const handleMonthSelected = ({ month, year }: MonthValue) => {
  firstVisibleMonth.value = clampFirstVisibleMonth(new Date(year, month, 1));
  monthYearView.value = null;
};
</script>

<style scoped lang="scss">
.scroll-button {
  @apply flex size-[2rem] items-center justify-center rounded-full hover:bg-obsidian-600 hover:text-white disabled:pointer-events-none disabled:opacity-30;
}
</style>

<i18n lang="json">
{
  "en": {
    "previousMonth": "Previous month",
    "nextMonth": "Next month",
    "time": "Time",
    "start": "Start",
    "end": "End"
  },
  "de": {
    "previousMonth": "Vorheriger Monat",
    "nextMonth": "Nächster Monat",
    "time": "Uhrzeit",
    "start": "Start",
    "end": "Ende"
  },
  "es": {
    "previousMonth": "Mes anterior",
    "nextMonth": "Mes siguiente",
    "time": "Hora",
    "start": "Inicio",
    "end": "Fin"
  }
}
</i18n>
